/**
 * outputGate.js — F-116 generation OUTPUT gate (fail-closed), sibling to languageGate.js.
 *
 * PIPE-LANG-1 already checks that a story SAVES in the language it was told in — but it checked only the
 * BODY (the title was stripped before the check) and had no screen for model self-talk or a duplicated
 * inline title. F-116 (Spanish-class recurrence #6, founder test, thin-input post-F-103) shipped:
 *   TITLE  : "El Sabor del Chocolate"                      (Spanish title, body English → old gate passed)
 *   CONTENT: "Wait, I need to write in English.\nThe Taste of Chocolate\nThe taste of chocolate takes me..."
 *            (model self-talk leaked into the story + the English title duplicated inline)
 *
 * This gate hardens the output side for the newly-common thin-input path, checking the story the teller
 * actually sees BEFORE it is shown/saved:
 *   1. LANGUAGE on TITLE *and* BODY vs the SOURCE (the teller's own words). Legit bilingual still passes
 *      (source Spanish -> Spanish output is fine); 'unknown' on the source never blocks (multilingual-safe).
 *   2. SELF-TALK / meta screen — tightly scoped to meta-about-the-writing-task ("Wait, I need to write in
 *      English", "As an AI", "let me rewrite this in English"), NEVER generic first-person memoir prose.
 *   3. DUPLICATED-INLINE-TITLE — deterministic clean (the title repeated as the body's first line).
 *
 * Failure of (1) or (2) is a fail-closed gate trip (caller regenerates once with a corrective directive,
 * then falls back to the faithful Flick / the teller's own raw words). (3) is a safe deterministic clean,
 * not a failure — a cosmetic repeat should not discard an otherwise-good story.
 *
 * Reuses languageGate.detectStoryLanguage (one language detector; no drift).
 */
import { detectStoryLanguage } from './languageGate.js';

// Self-talk / meta-commentary the model must never leak into a saved keepsake. Every pattern is anchored
// on the WRITING/LANGUAGE TASK or an AI-identity/refusal tell — NOT bare first-person verbs, so legitimate
// memoir ("I need to tell you about her", "let me take you back", "I should have called") never trips.
export const SELF_TALK_PATTERNS = [
  // "Wait, I need to write / redo / start over ..." (the F-116 live tell)
  /\bwait,?\s+(i|let me)\b[^.\n]{0,40}\b(write|redo|rewrite|start over|try again|do this|use|switch)\b/i,
  // "I need/should/have to write (this) in <language>"
  /\bi\s+(need|have|should|must|'?ll\s+need)\s+to\s+(re)?write\b[^.\n]{0,40}\bin\s+[a-z]+/i,
  // "let me rewrite/redo this ..." — F-119: bare translate/correct/fix DROPPED (they false-positive on
  // legit memoir, e.g. "Let me translate for my grandfather was my job"); the anchored form
  // ("translate/correct THIS into English") is covered by the next pattern.
  /\blet me\s+(rewrite|redo|re-?do|start over|try again|write this)\b/i,
  // "(let me) translate/rewrite/correct this into English"
  /\b(translate|rewrite|re-?write|correct)\s+(this|that|it)\b[^.\n]{0,20}\b(in|to|into)\s+[a-z]+/i,
  // "here's the rewritten / corrected / (English) / translated version|story|title" (allow an adjective between)
  /\bhere'?s?\s+(is\s+)?(the|your|a)\s+((rewritten|corrected|revised|english|spanish|translated|proper)\s+)+(version|story|title|text)/i,
  // AI-identity / model self-reference
  /\bas an ai\b/i,
  /\b(i'?m|i am)\s+an\s+ai\b/i,
  /\bas a language model\b/i,
  /\b(an?\s+)?ai\s+(language\s+)?(model|assistant)\b/i,
  // Refusal / instruction leakage
  /\bi\s+(can'?t|cannot|won'?t|will not)\s+(write|help|assist|do that|complete)\b/i,
  /\b(ignore|disregard)\s+(the|my|all|any|previous)\b[^.\n]{0,20}\b(instruction|prompt|above)/i,
];

/** Screen a single string for model self-talk. Returns { hit, matched }. */
export function hasSelfTalk(text) {
  const s = text || '';
  for (const re of SELF_TALK_PATTERNS) {
    const m = s.match(re);
    if (m) return { hit: true, matched: m[0] };
  }
  return { hit: false, matched: null };
}

/** Normalize a line for title-duplication comparison (strip markdown, punctuation, case, whitespace). */
function normalizeForTitle(s) {
  return (s || '')
    .replace(/^#+\s*/, '')
    .replace(/[*_`"'“”‘’.]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Remove a duplicated inline title: if the body's first non-empty line equals the title (after
 * normalization), drop it. Deterministic and safe — only removes an exact repeat of the known title.
 * Returns the cleaned content (unchanged when there is no duplicate).
 */
export function stripDuplicateTitle(title, content) {
  const nt = normalizeForTitle(title);
  if (!nt) return content || '';
  const lines = (content || '').split('\n');
  let i = 0;
  while (i < lines.length && !lines[i].trim()) i++; // skip leading blank lines
  if (i < lines.length && normalizeForTitle(lines[i]) === nt) {
    lines.splice(i, 1);
    // also drop a now-leading blank line left behind
    while (i < lines.length && !lines[i].trim()) lines.splice(i, 1);
  }
  return lines.join('\n').replace(/^\n+/, '').trimEnd();
}

/**
 * Language check on TITLE and BODY vs the SOURCE. Blocks only a CONFIDENT mismatch on either field.
 * 'unknown' source never blocks (multilingual-safe); a too-short 'unknown' title/body is not a mismatch.
 */
export function outputLanguageCheck({ sourceText, title, content }) {
  const sourceLang = detectStoryLanguage(sourceText);
  if (sourceLang === 'unknown') return { ok: true, sourceLang, titleLang: 'unknown', bodyLang: 'unknown', titleBad: false, bodyBad: false };
  const titleLang = detectStoryLanguage(title);
  const bodyLang = detectStoryLanguage(content);
  const titleBad = titleLang !== 'unknown' && titleLang !== sourceLang;
  const bodyBad = bodyLang !== 'unknown' && bodyLang !== sourceLang;
  return { ok: !(titleBad || bodyBad), sourceLang, titleLang, bodyLang, titleBad, bodyBad };
}

/**
 * The composite output gate. Runs the deterministic dup-title clean first, then the language + self-talk
 * checks on the CLEANED story. Returns:
 *   { ok, reasons[], cleanTitle, cleanContent, detail }
 * `ok=false` (reasons include 'language-title' | 'language-body' | 'self-talk') means the caller must
 * regenerate once with a corrective directive, then fail closed to a language-safe faithful version.
 * `cleanTitle`/`cleanContent` always carry the dup-title clean, so even an ok story is de-duplicated.
 */
export function gateStoryOutput({ sourceText, title, content }) {
  const cleanContent = stripDuplicateTitle(title, content);
  const cleanTitle = title || '';
  const lang = outputLanguageCheck({ sourceText, title: cleanTitle, content: cleanContent });
  const stTitle = hasSelfTalk(cleanTitle);
  const stBody = hasSelfTalk(cleanContent);
  const reasons = [];
  if (lang.titleBad) reasons.push('language-title');
  if (lang.bodyBad) reasons.push('language-body');
  if (stTitle.hit || stBody.hit) reasons.push('self-talk');
  return { ok: reasons.length === 0, reasons, cleanTitle, cleanContent, detail: { lang, stTitle, stBody } };
}

const LANGUAGE_NAME = { en: 'English', es: 'Spanish' };

/**
 * Corrective regen directive appended to the polish prompt on a gate trip. Forces the SOURCE language
 * (never hardcoded English — TB-097) and forbids meta/self-talk + a repeated title. Composes both fixes.
 */
export function correctiveRegenDirective({ sourceLang, reasons = [] } = {}) {
  const parts = [];
  const name = LANGUAGE_NAME[sourceLang];
  if (name && (reasons.includes('language-title') || reasons.includes('language-body'))) {
    parts.push(`The person spoke in ${name}. Write the TITLE and the ENTIRE story in ${name}. Do NOT translate it into any other language.`);
  }
  if (reasons.includes('self-talk')) {
    parts.push('Write ONLY the finished story. Do NOT include any note, correction, apology, or commentary about the writing itself (for example: "Wait, I need to write in English", "as an AI", "here is the rewritten version"). Output one title line, then the story — and never repeat the title inside the body.');
  }
  if (!parts.length) return '';
  return `\n\nCRITICAL OUTPUT RULE: ${parts.join(' ')}`;
}
